import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthContext } from '../../authContext';
import AlertsCustom from '../Alerts';
import { translateBackendError } from '../../utils/translateBackendError';
import useEscapeToClose from '../../hooks/useEscapeToClose';
import './ShareSkillModal.css';

// Opcoes do modal "Compartilhar skill" (Tarefa 4.1, specs/skills/plan.md) -
// nomes/textos e ordem (Minhas skills, Compartilhar, TPF) fieis ao mockup do
// Figma (node 24:1137), que usa o titulo "Compartilhar skill" e o botao
// "Concluir" - divergem da redacao original da spec/plan ("Fazer upload de
// skill" / "Concluir upload"), decisao tomada em conjunto com o usuario para
// priorizar fidelidade visual ao Figma. "Compartilhar" (visibility "share")
// abre a busca de pessoas/times especificos (Tarefa 4.2, node 24:1265).
const VISIBILITY_OPTIONS = [
    {
        value: 'private',
        labelKey: 'skills_share_option_private_label',
        descKey: 'skills_share_option_private_desc',
    },
    {
        value: 'share',
        labelKey: 'skills_share_option_share_label',
        descKey: 'skills_share_option_share_desc',
    },
    // {
    //     value: 'public',
    //     labelKey: 'skills_share_option_public_label',
    //     descKey: 'skills_share_option_public_desc',
    // },
];

// Normaliza para a busca: sem acento, minusculo e com . _ - virando espaco -
// assim "Joao" acha "João" e "ricardo lugano" acha quem ainda esta cadastrado
// com o e-mail ("ricardo.lugano@tpfe.com.br") como nome.
function normalizeForSearch(text) {
    return (text || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

// Filtro por texto da busca de pessoas/times (Tarefa 4.2) - substring no
// nome (e, para pessoas, tambem no e-mail), ignorando maiusculas e acentos.
function matchesShareQuery(target, term) {
    const normalized = normalizeForSearch(term);
    if (!normalized) return true;
    return normalizeForSearch(target.name).includes(normalized)
        || (target.type === 'user' && normalizeForSearch(target.id).includes(normalized));
}

function sameTarget(a, b) {
    return a.type === b.type && a.id === b.id;
}

// Limite de caracteres do chip da lista de pessoas compartilhadas (Tarefa
// de uniformizacao, 2026-09-02) - acima disso trunca com "..." e mostra o
// nome completo no title (tooltip nativo do navegador).
const CHIP_NAME_CHAR_LIMIT = 20;

function truncateChipName(name) {
    return name.length > CHIP_NAME_CHAR_LIMIT
        ? `${name.slice(0, CHIP_NAME_CHAR_LIMIT)}...`
        : name;
}

const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ShareSkillModal({ isOpen, onClose, skill, onUpdated, onError = () => { } }) {
    const { t } = useTranslation('chat');
    const { email, accessToken } = useContext(AuthContext);

    const [selected, setSelected] = useState('private');

    // Erro de validacao sincrono de addShareTargetByEmail (e-mail mal
    // formatado) - unico caso deste modal que precisa de alerta local: o
    // fluxo de submissao (handleConfirm) e otimista e fecha o modal antes da
    // resposta do backend, por isso usa onError (prop, avisa o pai que
    // continua montado) em vez de um alerta local que nunca seria visto.
    const [alert, setAlert] = useState(null);

    // Tarefa 5.6: enquanto GET /get_skill_shares nao responde, `selected`
    // ainda esta no valor inicial ('private') - sem esse estado, o modal
    // exibia "Minhas skills" marcado por um instante mesmo quando a skill
    // real e publica ou compartilhada (flash de selecao errada). Comeca
    // `true` (nao `false`) para nao piscar as opcoes reais no primeiro
    // render de cada abertura - so o efeito abaixo desliga.
    const [visibilityLoading, setVisibilityLoading] = useState(true);

    // Tarefa 4.2: alvos (pessoas/times) atualmente compartilhados e
    // disponiveis para escolha. update_visibility grava visibility='private'
    // no SQL mesmo quando visibility='share' (o acesso extra vive so em
    // skill_shares) - por isso o estado inicial de `selected` depende de
    // GET /get_skill_shares, nao so de `skill.visibility`.
    const [selectedShares, setSelectedShares] = useState([]);
    const [shareTargets, setShareTargets] = useState(null);
    const [shareTargetsLoading, setShareTargetsLoading] = useState(false);
    const [shareQuery, setShareQuery] = useState('');
    const [emailToAdd, setEmailToAdd] = useState('');

    const [allowedDomains, setAllowedDomains] = useState([]);
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/get_domains`)
            .then((response) => response.json())
            .then((data) => {
                setAllowedDomains((data || []).map((d) => (d.dominio || '').toLowerCase().trim()));
            })
            .catch(() => {});
    }, []);

    // Acompanha quem foi adicionado por ultimo na lista de chips - sem isso,
    // a lista (com scroll vertical, Tarefa de uniformizacao 2026-09-02) fica
    // parada no topo conforme mais pessoas entram, escondendo quem acabou de
    // ser selecionado la embaixo.
    const chipsListRef = useRef(null);
    useEffect(() => {
        if (chipsListRef.current) {
            chipsListRef.current.scrollTop = chipsListRef.current.scrollHeight;
        }
    }, [selectedShares]);

    // Reseta a selecao para o estado atual da skill sempre que o modal abre -
    // "Minhas skills" pre-selecionado para uma skill privada (padrao de toda
    // skill nova, FR-008), "TPF" se ja publica (User Story 4, cenario 1), ou
    // "Compartilhar" (com os chips pre-preenchidos) se a skill ja tiver
    // compartilhamentos registrados em skill_shares (Tarefa 4.2).
    useEffect(() => {
        if (!isOpen || !skill?.skill_id) return;
        let cancelled = false;
        setAlert(null);
        setVisibilityLoading(true);

        const fallback = () => {
            setSelected(skill?.visibility === 'public' ? 'public' : 'private');
            setSelectedShares([]);
        };

        fetch(`${process.env.REACT_APP_API_URL}/get_skill_shares?email=${encodeURIComponent(email)}&skill_id=${encodeURIComponent(skill.skill_id)}`, {
            headers: { 'Authorization': `Bearer ${accessToken}` },
        })
            .then((response) => (response.ok ? response.json() : { shares: [] }))
            .then((data) => {
                if (cancelled) return;
                const shares = data.shares || [];
                if (shares.length > 0) {
                    setSelected('share');
                    setSelectedShares(shares);
                } else {
                    fallback();
                }
            })
            .catch(() => {
                if (!cancelled) fallback();
            })
            .finally(() => {
                if (!cancelled) setVisibilityLoading(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, skill]);

    // Busca os alvos disponiveis para o picker (Tarefa 4.2) so na primeira
    // vez que "Compartilhar" fica selecionado - cache em estado local,
    // resetado ao fechar o modal (mesmo padrao de cache-por-abertura do
    // SkillsManagerModal).
    useEffect(() => {
        if (!isOpen || selected !== 'share' || shareTargets !== null) return;
        let cancelled = false;
        setShareTargetsLoading(true);

        fetch(`${process.env.REACT_APP_API_URL}/get_skill_share_targets?email=${encodeURIComponent(email)}`, {
            headers: { 'Authorization': `Bearer ${accessToken}` },
        })
            .then((response) => (response.ok ? response.json() : { targets: [] }))
            .then((data) => {
                if (!cancelled) setShareTargets(data.targets || []);
            })
            .catch(() => {
                if (!cancelled) setShareTargets([]);
            })
            .finally(() => {
                if (!cancelled) setShareTargetsLoading(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, selected, shareTargets]);

    // Reseta o cache de alvos/busca ao fechar - a proxima abertura busca de
    // novo (mesmo padrao de reset do SkillsManagerModal).
    useEffect(() => {
        if (!isOpen) {
            setShareTargets(null);
            setShareTargetsLoading(false);
            setShareQuery('');
            setEmailToAdd('');
            setAlert(null);
            // Tarefa 5.6: volta pro estado "carregando" enquanto fechado, para
            // a proxima abertura ja renderizar o spinner desde o primeiro
            // frame (sem isso, o primeiro render da proxima abertura reusaria
            // `visibilityLoading=false` da skill anterior antes do efeito de
            // busca ter chance de rodar, causando um flash do valor antigo).
            setVisibilityLoading(true);
        }
    }, [isOpen]);

    useEscapeToClose(onClose, isOpen);

    if (!isOpen) return null;

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const toggleShareTarget = (target) => {
        setSelectedShares((prev) => (
            prev.some((t) => sameTarget(t, target))
                ? prev.filter((t) => !sameTarget(t, target))
                : [...prev, target]
        ));
    };

    const addShareTargetByEmail = () => {
        const emailValue = emailToAdd.trim().toLowerCase();
        if (!EMAIL_FORMAT_RE.test(emailValue)) {
            setAlert({
                type: 'error',
                title: t('skills_share_error_title'),
                message: t('skills_share_add_email_invalid'),
            });
            return;
        }

        const domain = emailValue.split('@')[1];
        if (!allowedDomains.includes(domain)) {
            setAlert({
                type: 'error',
                title: t('skills_share_error_title'),
                message: t('skills_share_add_email_domain_not_allowed'),
            });
            return;
        }

        if (shareTargetsLoading || shareTargets === null) return;

        const matchedTarget = (shareTargets || []).find(
            (target) => target.type === 'user' && target.id === emailValue,
        ) || { type: 'user', id: emailValue, name: emailValue, subtitle: emailValue };

        setSelectedShares((prev) => (
            prev.some((t) => sameTarget(t, matchedTarget)) ? prev : [...prev, matchedTarget]
        ));
        setEmailToAdd('');
    };

    // Tarefa 5.10: fecha e aplica a mudanca de visibilidade de forma
    // otimista (sem esperar POST /post_skill_visibility responder) - antes,
    // o "Concluir" so fechava o modal depois da rota responder, o que
    // deixava a tela parada/travada quando a rota demorava. O card na grade
    // ja reflete `selected`/`selectedShares` no instante do clique; a
    // requisicao continua em segundo plano e, se falhar, `onError` avisa o
    // SkillsManagerModal (que continua montado, diferente deste modal) para
    // mostrar o alerta de erro mesmo com o modal ja fechado.
    const handleConfirm = () => {
        // Sem ninguem selecionado, "Compartilhar" cai para "Minhas skills" -
        // decisao do usuario (2026-08-12): silencioso, sem bloqueio/alerta,
        // mesmo que a skill ja tivesse compartilhamentos ativos (a lista
        // vazia e tratada como intencao de remover todo mundo).
        const effectiveVisibility = selected === 'share' && selectedShares.length === 0
            ? 'private'
            : selected;

        const optimisticSkill = {
            ...skill,
            assistant_scoped: false,
            visibility: effectiveVisibility === 'public' ? 'public' : 'private',
        };
        onUpdated?.(optimisticSkill);
        onClose();

        const body = { email, skill_id: skill.skill_id, visibility: effectiveVisibility };
        if (effectiveVisibility === 'share') {
            body.shares = selectedShares.map((target) => (
                target.type === 'group' ? { group_id: target.id } : { user_email: target.id }
            ));
        }

        fetch(`${process.env.REACT_APP_API_URL}/post_skill_visibility`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify(body),
        })
            .then(async (response) => {
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    onError(translateBackendError(data.error) || t('skills_share_error_title'));
                    return;
                }
                onUpdated?.(data);
            })
            .catch(() => {
                onError(t('skills_share_error_title'));
            });
    };

    const filteredTargets = (shareTargets || [])
        .filter((target) => !selectedShares.some((t) => sameTarget(t, target)))
        .filter((target) => matchesShareQuery(target, shareQuery));

    return (
        <div
            className="modal-overlay-share-skill"
            onClick={handleOverlayClick}
            data-testid="share-skill-overlay"
        >
            <div className="modal-content-share-skill" data-testid="share-skill-modal">
                <div className="share-skill-header">
                    <p className="share-skill-title">{t('skills_share_modal_title')}</p>
                    <button
                        type="button"
                        className="share-skill-close"
                        aria-label={t('closeIcon')}
                        onClick={onClose}
                        data-testid="share-skill-close"
                    >
                        <img src="/imagens/x.svg" alt="" />
                    </button>
                </div>

                {visibilityLoading ? (
                    <div className="share-skill-visibility-loading" data-testid="share-skill-visibility-loading">
                        <CircularProgress size={24} thickness={4} />
                    </div>
                ) : (
                <>
                <div className="share-skill-options">
                    <p className="share-skill-question">{t('skills_share_modal_question')}</p>

                    {VISIBILITY_OPTIONS.map((option) => (
                        <label
                            key={option.value}
                            className={
                                'share-skill-option'
                                + (selected === option.value ? ' share-skill-option--selected' : '')
                            }
                            data-testid={`share-skill-option-${option.value}`}
                        >
                            <input
                                type="radio"
                                name="share-skill-visibility"
                                className="share-skill-radio-input"
                                checked={selected === option.value}
                                onChange={() => setSelected(option.value)}
                            />
                            <span className="share-skill-radio-visual" aria-hidden="true" />
                            <span className="share-skill-option-text">
                                <span className="share-skill-option-label">{t(option.labelKey)}</span>
                                {' '}
                                <span className="share-skill-option-desc">{t(option.descKey)}</span>
                            </span>
                        </label>
                    ))}
                </div>

                {selected === 'share' && (
                    <div className="share-skill-search-section" data-testid="share-skill-search-section">
                        <p className="share-skill-question">{t('skills_share_search_label')}</p>
                        <div className="share-skill-search-box-wrapper">
                            <div className="share-skill-inputs-row">
                                <div className="share-skill-search-input-container">
                                    <img src="/imagens/search.svg" alt="" className="share-skill-search-icon" />
                                    <input
                                        type="text"
                                        className="share-skill-search-input"
                                        placeholder={t('skills_share_search_placeholder')}
                                        value={shareQuery}
                                        onChange={(e) => setShareQuery(e.target.value)}
                                        data-testid="share-skill-search-input"
                                    />
                                </div>

                                <div className="share-skill-add-email-input-container">
                                    <input
                                        type="text"
                                        className="share-skill-add-email-input"
                                        placeholder={t('skills_share_add_email_placeholder')}
                                        value={emailToAdd}
                                        onChange={(e) => setEmailToAdd(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') addShareTargetByEmail();
                                        }}
                                        data-testid="share-skill-add-email-input"
                                    />
                                    <img
                                        src="/imagens/e-mail_icon.svg"
                                        alt=""
                                        className="share-skill-add-email-icon"
                                        onClick={addShareTargetByEmail}
                                        data-testid="share-skill-add-email-button"
                                    />
                                </div>
                            </div>

                            {selectedShares.length > 0 && (
                                <div className="share-skill-chips" ref={chipsListRef}>
                                    {selectedShares.map((target) => (
                                        <div
                                            key={`${target.type}-${target.id}`}
                                            className="share-skill-chip"
                                            data-testid="share-skill-chip"
                                        >
                                            <span
                                                className={`share-skill-chip-icon${target.type === 'group' ? ' share-skill-chip-icon--group' : ''}`}
                                                aria-hidden="true"
                                            >
                                                {target.type === 'group'
                                                    ? <img src="/imagens/three-users.svg" alt="" />
                                                    : target.name.charAt(0).toUpperCase()}
                                            </span>
                                            <span className="share-skill-chip-name" title={target.name}>
                                                {truncateChipName(target.name)}
                                            </span>
                                            <button
                                                type="button"
                                                className="share-skill-chip-remove"
                                                aria-label={t('skills_share_remove_chip')}
                                                onClick={() => toggleShareTarget(target)}
                                                data-testid="share-skill-chip-remove"
                                            >
                                                <img src="/imagens/x.svg" alt="" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="share-skill-target-list">
                                {shareTargetsLoading ? (
                                    <div className="share-skill-targets-loading" data-testid="share-skill-targets-loading">
                                        <CircularProgress size={24} thickness={4} />
                                    </div>
                                ) : filteredTargets.length === 0 ? (
                                    <p className="share-skill-targets-empty" data-testid="share-skill-targets-empty">
                                        {t('skills_share_targets_empty')}
                                    </p>
                                ) : (
                                    filteredTargets.map((target) => {
                                        return (
                                            <button
                                                type="button"
                                                key={`${target.type}-${target.id}`}
                                                className="share-skill-target-item"
                                                onClick={() => toggleShareTarget(target)}
                                                data-testid="share-skill-target-item"
                                            >
                                                <span
                                                    className={`share-skill-target-icon${target.type === 'group' ? ' share-skill-target-icon--group' : ''}`}
                                                    aria-hidden="true"
                                                >
                                                    {target.type === 'group'
                                                        ? <img src="/imagens/three-users.svg" alt="" />
                                                        : target.name.charAt(0).toUpperCase()}
                                                </span>
                                                <span className="share-skill-target-texts">
                                                    <span className="share-skill-target-name">{target.name}</span>
                                                    <span className="share-skill-target-subtitle">{target.subtitle}</span>
                                                </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    className="share-skill-confirm-button"
                    onClick={handleConfirm}
                    data-testid="share-skill-confirm"
                >
                    {t('skills_share_confirm_button')}
                </button>
                </>
                )}
            </div>

            {alert && (
                <AlertsCustom
                    type={alert.type}
                    title={alert.title}
                    message={alert.message}
                    onClose={() => setAlert(null)}
                />
            )}
        </div>
    );
}

export default ShareSkillModal;
